import { c as _c } from "react/compiler-runtime";
import { Divider, Form, type TemplateInputProps } from '@onefootprint/ui';
import capitalize from 'lodash/capitalize';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import type { VendorIntegrationFormData } from '../../types';
import { formFieldKey, getIntegrationInputNodes, type IntegrationInputNode, pathKey } from '../../utils/integration-schema';
type FieldPrefix = 'config.integrationInputs' | 'config.integrationMockData';
type IntegrationInputsFormProps = {
  inputSchema: unknown;
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  fieldPrefix?: FieldPrefix;
  emptyMessageKey?: 'noInputs' | 'noMockFields';
};
const IntegrationInputsForm = t0 => {
  const $0 = _c(19);
  const {
    inputSchema,
    autocompleteItems,
    fieldPrefix: t1,
    emptyMessageKey: t2
  } = t0;
  const fieldPrefix = t1 === undefined ? "config.integrationInputs" : t1;
  const emptyMessageKey = t2 === undefined ? "noInputs" : t2;
  let t3;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      keyPrefix: "externalApi.vendorIntegration"
    };
    $0[0] = t3;
  } else {
    t3 = $0[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t3);
  let t4;
  let t5;
  let t6;
  if ($0[1] !== autocompleteItems || $0[2] !== emptyMessageKey || $0[3] !== fieldPrefix || $0[4] !== inputSchema || $0[5] !== t) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const nodes = getIntegrationInputNodes(inputSchema);
      if (nodes.length === 0) {
        let t7;
        if ($0[9] !== emptyMessageKey) {
          t7 = $ => $[emptyMessageKey];
          $0[9] = emptyMessageKey;
          $0[10] = t7;
        } else {
          t7 = $0[10];
        }
        let t8;
        if ($0[11] !== t || $0[12] !== t7) {
          t8 = t(t7);
          $0[11] = t;
          $0[12] = t7;
          $0[13] = t8;
        } else {
          t8 = $0[13];
        }
        let t9;
        if ($0[14] !== t8) {
          t9 = <p className="text-body-3 text-tertiary">{t8}</p>;
          $0[14] = t8;
          $0[15] = t9;
        } else {
          t9 = $0[15];
        }
        t6 = t9;
        break bb0;
      }
      t4 = "flex flex-col p-3 border border-tertiary rounded-md";
      t5 = nodes.map((node, index) => <Row key={pathKey(node.path)} node={node} autocompleteItems={autocompleteItems} fieldPrefix={fieldPrefix} isLast={index === nodes.length - 1} />);
    }
    $0[1] = autocompleteItems;
    $0[2] = emptyMessageKey;
    $0[3] = fieldPrefix;
    $0[4] = inputSchema;
    $0[5] = t;
    $0[6] = t4;
    $0[7] = t5;
    $0[8] = t6;
  } else {
    t4 = $0[6];
    t5 = $0[7];
    t6 = $0[8];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($0[16] !== t4 || $0[17] !== t5) {
    t7 = <div className={t4}>{t5}</div>;
    $0[16] = t4;
    $0[17] = t5;
    $0[18] = t7;
  } else {
    t7 = $0[18];
  }
  return t7;
};
const Row = t0 => {
  const $ = _c(9);
  const {
    node,
    autocompleteItems,
    fieldPrefix,
    isLast
  } = t0;
  let t1;
  if ($[0] !== autocompleteItems || $[1] !== fieldPrefix || $[2] !== node) {
    t1 = node.kind === "group" ? <GroupRow node={node} /> : <LeafRow node={node} autocompleteItems={autocompleteItems} fieldPrefix={fieldPrefix} />;
    $[0] = autocompleteItems;
    $[1] = fieldPrefix;
    $[2] = node;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== isLast) {
    t2 = !isLast && <Divider />;
    $[4] = isLast;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const GroupRow = t0 => {
  const $ = _c(16);
  const {
    node
  } = t0;
  const name = node.path[node.path.length - 1];
  const t1 = `${node.depth * 16}px`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      paddingLeft: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== name) {
    t3 = startCase(name);
    $[2] = name;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <span className="text-label-3 text-primary font-semibold">{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <TypeBadge type="object" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== node.description) {
    t6 = node.description && <DescriptionText description={node.description} />;
    $[7] = node.description;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== node.required) {
    t7 = node.required && <RequiredLabel />;
    $[9] = node.required;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t4 || $[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex items-center gap-2 py-3" style={t2}>{t4}{t5}{t6}{t7}</div>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
const LeafRow = t0 => {
  const $ = _c(32);
  const {
    node,
    autocompleteItems,
    fieldPrefix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "externalApi.vendorIntegration"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const name = node.path[node.path.length - 1];
  let t2;
  if ($[1] !== node.path) {
    t2 = formFieldKey(node.path);
    $[1] = node.path;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const fieldName = `${fieldPrefix}.${t2}` as const;
  const t3 = `${node.depth * 16}px`;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      paddingLeft: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== name) {
    t5 = startCase(name);
    $[5] = name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <span className="text-body-3 text-primary font-semibold">{t5}</span>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== node.type) {
    t7 = <TypeBadge type={node.type} />;
    $[9] = node.type;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== node.required) {
    t8 = node.required && <RequiredLabel />;
    $[11] = node.required;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = <span className="flex items-center gap-1.5 min-w-0">{t6}{t7}{t8}</span>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== node.description) {
    t10 = node.description && <DescriptionText description={node.description} />;
    $[17] = node.description;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div className="flex flex-col gap-0.5 min-w-0">{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t(_temp);
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== autocompleteItems || $[25] !== fieldName || $[26] !== t12) {
    t13 = <Form.TemplateInput name={fieldName} placeholder={t12} autocompleteItems={autocompleteItems} size="compact" />;
    $[24] = autocompleteItems;
    $[25] = fieldName;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t13 || $[30] !== t4) {
    t14 = <div className="flex flex-col gap-2 py-3" style={t4}>{t11}{t13}</div>;
    $[28] = t11;
    $[29] = t13;
    $[30] = t4;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};
const DescriptionText = t0 => {
  const $ = _c(4);
  const {
    description
  } = t0;
  let t1;
  if ($[0] !== description) {
    t1 = capitalize(description);
    $[0] = description;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="text-caption-2 text-tertiary">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const TypeBadge = t0 => {
  const $ = _c(2);
  const {
    type
  } = t0;
  let t1;
  if ($[0] !== type) {
    t1 = <span className="px-1.5 py-0.5 rounded text-caption-3 bg-secondary text-tertiary font-normal font-mono">{type}</span>;
    $[0] = type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const RequiredLabel = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "externalApi.vendorIntegration"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp2);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="ml-auto text-caption-2 font-bold text-error">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export default IntegrationInputsForm;
function _temp($) {
  return $.valuePlaceholder;
}
function _temp2($) {
  return $.required;
}